import { HttpClient, HttpParams } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { QueryOptions } from '@orderna/admin-panel/src/types/query-options';
import { map, Observable } from 'rxjs';
import { ACCOUNTING_ANALYTICS_API_URL, ANALYTICS_API_URL } from '../accounting-analytics-api.constants';
import { IAllTransactionsApiService } from './all-transactions-api.interface';
import { Paginated } from '@orderna/admin-panel/src/types/pagination';
import { AllTransaction } from '../../../../model/all-transaction';
import { SuccessResponse } from '../../../shared/success-response';
import { AllTransactionApiMapper as Mapper } from './all-transaction-api.mapper';
import { SnackbarService } from '../../../../shared/services/snackbar.service';
import { ExportExcelPdfService } from '../../../export-excel-pdf/export-excel-pdf.service';

@Injectable({
  providedIn: 'root',
})
export class AllTransactionsApiService implements IAllTransactionsApiService {
  private http = inject(HttpClient);
  private readonly API_URL = `${ACCOUNTING_ANALYTICS_API_URL}/all-transactions`;
  
  constructor(private exportExcelPdfService: ExportExcelPdfService,
              private snackbarService: SnackbarService) {}
  
  getAllTransactions(options: QueryOptions<AllTransaction>): Observable<Paginated<AllTransaction>> {
    const params = new HttpParams({
      fromObject: Mapper.toFilterDto(options) as any,
    });

    return this.http
      .get<SuccessResponse>(this.API_URL, { params})
      .pipe(
        map((response) => Mapper.fromDto(response.data)),
      );
  }
  
  exportExcelPdfFile(options: any, fileType: string, print: boolean = false) {
    let formattedFromDate;
    let formattedToDate;

    if(options.dateFilter) {
    const [month, day, year] = options?.dateFilter?.split("_")[0].split("/");
    formattedFromDate = `${year}-${String(month).padStart(2, '0')}-${String(day).padStart(2, '0')}`;

    const [toMonth, toDay, toYear] = options?.dateFilter?.split("_")[1].split("/");
    formattedToDate = `${toYear}-${String(toMonth).padStart(2, '0')}-${String(toDay).padStart(2, '0')}`;
    }

    if(options.fromDate || options.toDate) {
      formattedFromDate = options.fromDate ;
      formattedToDate = options.toDate;
    }
    let params = new HttpParams()
      .set('fromDate', formattedFromDate)
      .set('toDate', formattedToDate);

    if (options.filters) {
      options.filters.forEach((filter:any) => {
        if (Array.isArray(filter.value)) {
          filter.value.forEach((val:any) => {
            params = params.append(filter.field, val);
          });
        } else if (filter.field === 'store' || filter.field === 'storeId') {
          const value = filter.value;
          params = params.set('storeId', value as string);
        } else {
          params = params.set(filter.field, filter.value);
        }
      });
    }

    if (options.status) {
      params = params.set('status', options.status);
    }

    const endpoint = fileType === 'pdf' ? 'export/all-transactions/pdf' : 'export/all-transactions/excel';
    const exportType = print ? 'print' : (fileType === 'pdf' ? 'pdf' : 'excel');

    this.http.post(`${ANALYTICS_API_URL}${endpoint}`, {}, {
      params,
      responseType: 'blob',
      observe: 'response'
    }).subscribe({
      next: (res) => {
        this.exportExcelPdfService.downloadOrPrintFile(res, fileType, print);
        // Notify that export completed successfully
        this.exportExcelPdfService['exportCompleteSubject'].next({ type: exportType, success: true });
      },
      error: (error) => {
        console.error(error);
        this.snackbarService.showError('Data not found');
        // Notify that export failed
        this.exportExcelPdfService['exportCompleteSubject'].next({ type: exportType, success: false });
      }
    });
  }
}
